<template>
  <div>
    <b-sidebar
      id="sidebar-backdrop"
      backdrop
      shadow
      bg-variant="white"
      sidebar-class="sidebar-lg"
      no-header
      right
      :visible="userEditSidebarActive"
      @change="(val) => $emit('update:user-edit-sidebar-active', val)"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            Editar Usuario
          </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <!-- Campos formulario -->
        <validation-observer ref="simpleRules">
          <b-form
            v-if="show"
            class="p-2"
            @submit="onSubmit"
            @reset="clearForm"
          >
            <!-- Nombre -->
            <validation-provider
              v-slot="{ errors }"
              name="nombre"
              rules="required"
            >
              <b-form-group
                label="Nombre:"
                label-for="input-name"
              >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  placeholder="Ingresa el nombre"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Correo -->
            <validation-provider
              v-slot="{ errors }"
              name="correo"
              rules="required|email"
            >
              <b-form-group
                label="Correo:"
                label-for="input-email"
              >
                <b-form-input
                  id="input-email"
                  v-model="form.mail"
                  autocomplete="new-password"
                  placeholder="Ingresa el correo"
                  required
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- ID de empleado -->
            <b-form-group
              label="ID de empleado:"
              label-for="input-employee-id"
            >
              <b-form-input
                id="input-employee-id"
                v-model="form.employee_id"
                placeholder="Ingresa el ID de empleado"
                :state="employeeIdState"
                @blur="validateEmployeeId"
              />
              <small
                v-if="employeeIdError"
                class="text-danger"
              >{{ employeeIdError }}</small>
            </b-form-group>

            <!-- Rol -->
            <validation-provider
              v-slot="{ errors }"
              name="rol"
              rules="required"
            >
              <b-form-group
                label="Rol:"
                label-for="select-rol"
              >
                <v-select
                  v-model="form.role"
                  autocomplete="new-password"
                  :options="roles"
                  :reduce="role => role.value"
                  :clearable="true"
                  placeholder="Selecciona un rol"
                  input-id="select-role"
                  required
                >
                  <template slot="no-options">
                    Lo siento, no se encontraron roles
                  </template>
                </v-select>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <!-- Meta de Venta -->
            <validation-provider
              v-slot="{ errors }"
              name="meta de venta"
              rules="decimal:2"
            >
              <b-form-group
                label="Meta de Venta (Q):"
                label-for="input-sales-target"
              >
                <b-form-input
                  id="input-sales-target"
                  v-model="form.sales_target"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ingresa la meta de venta"
                />
                <small class="text-danger">{{ errors[0] }}</small>
                <small class="text-muted">Dejar vacío si no aplica</small>
              </b-form-group>
            </validation-provider>

            <!-- Asistente IA -->
            <b-form-group label="Asistente IA:">
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  v-model="form.chat_enabled"
                  switch
                  class="mr-1"
                >
                  {{ form.chat_enabled ? 'Habilitado' : 'Deshabilitado' }}
                </b-form-checkbox>
              </div>
              <small class="text-muted">Permite o restringe el acceso al asistente IA para este usuario.</small>
            </b-form-group>

            <!-- Rutas asignadas -->
            <hr>
            <h5 class="mb-1">
              <feather-icon
                icon="MapIcon"
                size="18"
                class="mr-1"
              />
              Rutas asignadas ({{ assignedRoutes.length }})
            </h5>
            <div
              v-if="loadingRoutes"
              class="text-muted small mb-2"
            >
              Cargando rutas...
            </div>
            <div
              v-else-if="assignedRoutes.length === 0"
              class="text-muted small mb-2"
            >
              Este usuario no tiene rutas asignadas.
            </div>
            <ul
              v-else
              class="list-unstyled small mb-2"
            >
              <li
                v-for="r in assignedRoutes"
                :key="r.id"
                class="mb-25"
              >
                <feather-icon
                  icon="ArrowRightIcon"
                  size="12"
                />
                <strong>{{ r.name }}</strong>
                <small
                  v-if="r.customers_count != null"
                  class="text-muted"
                >
                  — {{ r.customers_count }} cliente(s)
                </small>
              </li>
            </ul>
            <b-button
              v-if="assignedRoutes.length > 0"
              variant="outline-warning"
              size="sm"
              class="mb-2"
              @click="openTransferModal"
            >
              <feather-icon
                icon="RepeatIcon"
                size="14"
                class="mr-25"
              />
              Transferir todas a otro usuario
            </b-button>

            <b-button
              type="submit"
              variant="primary"
            >
              Actualizar
            </b-button>
          </b-form>
        </validation-observer>

        <!-- Modal de transferencia masiva -->
        <b-modal
          v-model="transferModalVisible"
          title="Transferir rutas"
          size="md"
          hide-footer
          no-close-on-backdrop
        >
          <p class="mb-1">
            Vas a transferir <strong>{{ assignedRoutes.length }}</strong> ruta(s) de
            <strong>{{ form.name }}</strong> a otro usuario.
          </p>
          <p class="small text-muted">
            Todos los clientes vinculados a estas rutas pasarán al nuevo usuario automáticamente.
          </p>

          <b-form-group label="Usuario destino:">
            <v-select
              v-model="transferToUserId"
              :options="otherUsers"
              label="name"
              :reduce="u => u.id"
              placeholder="Selecciona el usuario destino"
              :clearable="true"
            >
              <template #option="{ name, role }">
                <div>
                  <span>{{ name }}</span>
                  <small
                    v-if="role"
                    class="text-muted ml-1"
                  >— {{ role === 'admin' ? 'Administrador' : 'Vendedor' }}</small>
                </div>
              </template>
            </v-select>
            <small class="text-muted">
              Si lo dejas vacío, las rutas quedarán sin usuario asignado.
            </small>
          </b-form-group>

          <div class="d-flex justify-content-end mt-2">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="transferModalVisible = false"
            >
              Cancelar
            </b-button>
            <b-button
              variant="warning"
              :disabled="transferring"
              @click="confirmTransfer"
            >
              {{ transferring ? 'Transfiriendo...' : 'Confirmar transferencia' }}
            </b-button>
          </div>
        </b-modal>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import vSelect from 'vue-select'
import { required } from '@/utils/validations/validations'

// Cache para evitar requests repetidos en edición
const editValidationCache = new Map()

// Agregar validación personalizada para employee_id único en edición
extend('employee_id_unique', {
  params: ['userId'],
  validate: async (value, { userId }) => {
    if (!value || value.trim() === '') return true // Si está vacío, no validar

    const trimmedValue = value.trim()
    const cacheKey = `${trimmedValue}-${userId}`

    // Verificar cache primero
    if (editValidationCache.has(cacheKey)) {
      return editValidationCache.get(cacheKey)
    }

    try {
      const response = await axios.get('system/users', {
        params: {
          query: trimmedValue,
          perPage: 1000, // Obtener más resultados para buscar
        },
      })

      // Si encuentra usuarios con ese employee_id, verificar que no sea el mismo usuario actual
      const users = response.data.data || []
      const existingUser = users.find(user => user.employee_id === trimmedValue)

      let result = true
      if (existingUser) {
        // Si existe pero es el mismo usuario que se está editando, es válido
        result = existingUser.id === parseInt(userId, 10)
      }

      // Guardar en cache
      editValidationCache.set(cacheKey, result)

      return result
    } catch (error) {
      return true // En caso de error, permitir continuar
    }
  },
  message: 'Este código de empleado ya está en uso',
})

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },

  model: {
    prop: 'userEditSidebarActive',
    event: 'update:user-edit-sidebar-active',
  },

  props: {
    userEditSidebarActive: {
      type: Boolean,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      required,

      form: {
        name: '',
        mail: '',
        employee_id: '',
        role: '',
        sales_target: '',
        chat_enabled: true,
      },
      show: true,
      employeeIdError: '',
      employeeIdState: null,

      roles: [
        { value: 'admin', label: 'Administrador' },
        { value: 'vendor', label: 'Vendedor' },
      ],

      assignedRoutes: [],
      loadingRoutes: false,
      allUsers: [],
      transferModalVisible: false,
      transferToUserId: null,
      transferring: false,
    }
  },

  computed: {
    otherUsers() {
      return this.allUsers.filter(u => u.id !== this.userId)
    },
  },

  watch: {
    userEditSidebarActive(val) {
      if (val) {
        this.getUser()
      }
    },
  },

  created() {
    this.getUser()
  },

  methods: {
    getUser() {
      axios
        .get(`system/users/${this.userId}`)
        .then(response => {
          this.form.name = response.data.data.name
          this.form.mail = response.data.data.email
          this.form.employee_id = response.data.data.employee_id || ''
          this.form.role = response.data.data.role
          this.form.sales_target = response.data.data.sales_target || ''
          this.form.chat_enabled = response.data.data.chat_enabled !== false

          // Reset employee_id validation state when loading user
          this.employeeIdError = ''
          this.employeeIdState = null

          this.loadAssignedRoutes()
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    loadAssignedRoutes() {
      this.loadingRoutes = true
      axios
        .get('customer-route', {
          params: {
            filterField: 'user_id',
            filterValue: this.userId,
            perPage: 9999,
          },
        })
        .then(response => {
          this.assignedRoutes = response.data.data || []
        })
        .catch(error => {
          this.showErrors(error)
        })
        .finally(() => {
          this.loadingRoutes = false
        })
    },

    loadAllUsers() {
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}&perPage=9999`)
        .then(response => {
          this.allUsers = response.data.data || []
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    openTransferModal() {
      this.transferToUserId = null
      this.loadAllUsers()
      this.transferModalVisible = true
    },

    confirmTransfer() {
      this.transferring = true
      axios
        .post('customer-route/transfer-vendor', {
          from_user_id: this.userId,
          to_user_id: this.transferToUserId,
        })
        .then(response => {
          const { transferred_routes: transferredRoutes, affected_customers: affectedCustomers } = response.data.data
          this.makeToast(
            'success',
            'Transferencia completada',
            `${transferredRoutes} ruta(s) y ${affectedCustomers} cliente(s) reasignados.`,
          )
          this.transferModalVisible = false
          this.loadAssignedRoutes()
        })
        .catch(error => {
          this.showErrors(error)
        })
        .finally(() => {
          this.transferring = false
        })
    },

    async validateEmployeeId() {
      if (!this.form.employee_id || this.form.employee_id.trim() === '') {
        this.employeeIdError = ''
        this.employeeIdState = null
        return
      }

      const trimmedValue = this.form.employee_id.trim()
      const cacheKey = `${trimmedValue}-${this.userId}`

      // Verificar cache primero
      if (editValidationCache.has(cacheKey)) {
        const result = editValidationCache.get(cacheKey)
        this.employeeIdError = result ? '' : 'Este código de empleado ya está en uso'
        this.employeeIdState = result
        return
      }

      try {
        const response = await axios.get('system/users', {
          params: {
            query: trimmedValue,
            perPage: 1000,
          },
        })

        const users = response.data.data || []
        const existingUser = users.find(user => user.employee_id === trimmedValue)

        let result = true
        if (existingUser) {
          // Si existe pero es el mismo usuario que se está editando, es válido
          result = existingUser.id === this.userId
        }

        // Guardar en cache
        editValidationCache.set(cacheKey, result)

        this.employeeIdError = result ? '' : 'Este código de empleado ya está en uso'
        this.employeeIdState = result
      } catch (error) {
        this.employeeIdError = ''
        this.employeeIdState = null
      }
    },

    onSubmit(event) {
      event.preventDefault()

      // Validar employee_id antes de enviar
      if (this.employeeIdError) {
        this.makeToast('danger', 'Error', 'Corrige los errores antes de continuar.')
        return
      }

      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          axios
            .put(`system/users/${this.userId}`, this.form)
            .then(response => {
              this.makeToast('success', 'Usuario actualizado', `Se ha actualizado el usuario ${response.data.data.name}.`)
              this.clearForm()
              this.$emit('update:user-edit-sidebar-active', false)
              this.$emit('refreshData')
            })
            .catch(error => {
              this.showErrors(error)
            })
        } else {
          this.makeToast('danger', 'Error', 'No es posible actualizar el usuario.')
        }
      })
    },

    clearForm() {
      this.form.name = ''
      this.form.mail = ''
      this.form.employee_id = ''
      this.form.role = ''
      this.form.sales_target = ''
      this.form.chat_enabled = true

      // Reset employee_id validation
      this.employeeIdError = ''
      this.employeeIdState = null

      this.show = false
      this.$nextTick(() => {
        this.show = true
      })
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },

    showErrors(error) {
      let errorMessage = 'Error al actualizar el usuario'

      if (error.response && error.response.data && error.response.data.errors) {
        const { errors } = error.response.data
        errorMessage = Object.values(errors).flat().join(', ')
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message
      }

      this.makeToast('danger', 'Error', errorMessage)
    },
  },
}
</script>
